import { useState } from 'react'
import { Search, Calendar, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateRangePicker } from '@/components/DateRangePicker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { RefundListParams } from '@/schemas/supplier'

interface RefundFilterBarProps {
  params: RefundListParams
  onParamsChange: (params: Partial<RefundListParams>) => void
}

// 申请类型选项
const applyTypeOptions = [
  { value: 'all', label: '全部类型' },
  { value: '1', label: '仅退款' },
  { value: '2', label: '退货退款' },
]

// 退款状态选项
const refundTypeOptions = [
  { value: 'all', label: '全部状态' },
  { value: '1', label: '仅退款' },
  { value: '2', label: '退货退款' },
  { value: '3', label: '已拒绝' },
  { value: '4', label: '待退货' },
  { value: '5', label: '退货中' },
  { value: '6', label: '已退款' },
]

export function RefundFilterBar({ params, onParamsChange }: RefundFilterBarProps) {
  const [showDatePicker, setShowDatePicker] = useState(false)

  const handleDateChange = (value: { start_date?: string; end_date?: string }) => {
    if (value.start_date && value.end_date) {
      onParamsChange({
        time: `${value.start_date} - ${value.end_date}`
      })
    } else {
      onParamsChange({ time: undefined })
    }
  }

  const clearDateFilter = () => {
    onParamsChange({ time: undefined })
    setShowDatePicker(false)
  }

  const clearAllFilters = () => {
    onParamsChange({
      order_id: undefined,
      time: undefined,
      apply_type: undefined,
      refund_type: undefined,
    })
  }

  const hasActiveFilters = params.order_id || params.time || params.apply_type !== undefined || params.refund_type !== undefined

  // Parse current date value for DateRangePicker
  const dateValue = params.time
    ? { start_date: params.time.split(' - ')[0], end_date: params.time.split(' - ')[1] }
    : undefined

  return (
    <div className="space-y-4">
      {/* 搜索和筛选 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {/* 订单号搜索 */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="搜索订单号..."
            value={params.order_id || ''}
            onChange={(e) => onParamsChange({ order_id: e.target.value || undefined })}
            className="pl-9"
          />
        </div>

        {/* 申请类型筛选 */}
        <Select
          value={params.apply_type !== undefined ? String(params.apply_type) : 'all'}
          onValueChange={(value) => onParamsChange({ apply_type: value === 'all' ? undefined : Number(value) })}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="申请类型" />
          </SelectTrigger>
          <SelectContent>
            {applyTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* 退款状态筛选 */}
        <Select
          value={params.refund_type !== undefined ? String(params.refund_type) : 'all'}
          onValueChange={(value) => onParamsChange({ refund_type: value === 'all' ? undefined : Number(value) })}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="退款状态" />
          </SelectTrigger>
          <SelectContent>
            {refundTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* 日期筛选 */}
        <div className="flex items-center gap-2">
          {showDatePicker || params.time ? (
            <div className="flex items-center gap-2">
              <DateRangePicker
                value={dateValue}
                onChange={handleDateChange}
              />
              {params.time && (
                <Button variant="ghost" size="icon" onClick={clearDateFilter}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDatePicker(true)}
            >
              <Calendar className="mr-1.5 h-4 w-4" />
              日期筛选
            </Button>
          )}
        </div>

        {/* 清除筛选 */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="mr-1.5 h-4 w-4" />
            清除筛选
          </Button>
        )}
      </div>
    </div>
  )
}
